/**
 * Active Reservations List Component
 * Displays current active reservations with details
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { 
  ClockIcon,
  EyeIcon,
  TruckIcon,
  UserIcon,
  CalendarIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

const ActiveReservationsList = ({ 
  reservations = [], 
  loading = false, 
  error = null,
  maxItems = 5,
  showTitle = true,
  customTitle = "Active Reservations",
  onReservationClick = null,
  showOverdueWarning = true,
  className = ""
}) => {
  const formatDate = (dateString) => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    if (isNaN(date.getTime()) || date.getFullYear() < 1970) {
      return '';
    }
    
    return date.toLocaleString();
  };

  const calculateExpectedReturn = (startTime, duration) => {
    if (!startTime || !duration) return '';
    
    const start = new Date(startTime);
    const expectedReturn = new Date(start.getTime() + (duration * 60 * 60 * 1000));
    
    return formatDate(expectedReturn);
  };

  const isOverdue = (startTime, duration) => {
    if (!startTime || !duration) return false;
    
    const now = new Date();
    const start = new Date(startTime);
    const expectedReturn = new Date(start.getTime() + (duration * 60 * 60 * 1000));
    
    return now > expectedReturn;
  };

  const handleReservationClick = (reservation) => {
    if (onReservationClick) {
      onReservationClick(reservation);
    }
  };

  const limitedReservations = reservations.slice(0, maxItems);

  if (loading) {
    return (
      <div className={`bg-white rounded-lg shadow ${className}`}>
        {showTitle && (
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">{customTitle}</h2>
          </div>
        )}
        <div className="p-6">
          <div className="space-y-4">
            {Array.from({ length: 3 }).map((_, index) => (
              <div key={index} className="animate-pulse">
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center space-x-4 mb-3">
                    <div className="h-4 bg-gray-200 rounded w-24"></div>
                    <div className="h-6 bg-gray-200 rounded-full w-16"></div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {Array.from({ length: 4 }).map((_, i) => (
                      <div key={i}>
                        <div className="h-3 bg-gray-200 rounded w-16 mb-1"></div>
                        <div className="h-4 bg-gray-200 rounded w-24"></div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`bg-white rounded-lg shadow ${className}`}>
        {showTitle && (
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">{customTitle}</h2>
          </div>
        )}
        <div className="p-6">
          <div className="text-center py-8">
            <ExclamationTriangleIcon className="h-12 w-12 text-red-400 mx-auto mb-4" />
            <p className="text-red-600 font-medium">Failed to load active reservations</p>
            <p className="text-red-500 text-sm mt-1">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-lg shadow ${className}`}>
      {showTitle && (
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-medium text-gray-900">{customTitle}</h2>
            {reservations.length > 0 && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                {reservations.length} active
              </span>
            )}
          </div>
        </div>
      )}
      
      <div className="p-6">
        {limitedReservations.length === 0 ? (
          <div className="text-center py-8">
            <ClockIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">No active reservations</p>
            <p className="text-sm text-gray-400 mt-1">All equipment is currently available</p>
          </div>
        ) : (
          <div className="space-y-4">
            {limitedReservations.map((reservation) => {
              const overdue = isOverdue(reservation.startTime, reservation.reservationDuration);
              
              return (
                <div 
                  key={reservation.id} 
                  className={`border rounded-lg p-4 transition-colors duration-200 ${
                    overdue 
                      ? 'border-red-200 bg-red-50 hover:bg-red-100' 
                      : 'border-gray-200 hover:bg-gray-50'
                  } ${onReservationClick ? 'cursor-pointer' : ''}`}
                  onClick={() => handleReservationClick(reservation)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-4 mb-3">
                        <span className="text-sm font-medium text-gray-900">
                          {reservation.reservationNumber}
                        </span>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          overdue 
                            ? 'bg-red-100 text-red-800' 
                            : 'bg-blue-100 text-blue-800'
                        }`}>
                          {overdue ? (
                            <>
                              <ExclamationTriangleIcon className="h-3 w-3 mr-1" />
                              OVERDUE
                            </>
                          ) : (
                            'ACTIVE'
                          )}
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <div className="flex items-center text-gray-500 mb-1">
                            <UserIcon className="h-3 w-3 mr-1" />
                            Customer
                          </div>
                          <p className="font-medium text-gray-900">
                            {reservation.customer?.firstName} {reservation.customer?.lastName}
                          </p>
                        </div>
                        
                        <div>
                          <div className="flex items-center text-gray-500 mb-1">
                            <TruckIcon className="h-3 w-3 mr-1" />
                            'Item'
                          </div>
                          <p className="font-medium text-gray-900">
                            {reservation.golfCart?.cartNumber || 
                             reservation.inventoryItem?.name || 
                             reservation.service?.name || 'N/A'}
                          </p>
                        </div>
                        
                        <div>
                          <div className="flex items-center text-gray-500 mb-1">
                            <ClockIcon className="h-3 w-3 mr-1" />
                            Duration
                          </div>
                          <p className="font-medium text-gray-900">
                            {reservation.reservationDuration} hour{reservation.reservationDuration !== 1 ? 's' : ''}
                          </p>
                        </div>
                        
                        <div>
                          <div className="flex items-center text-gray-500 mb-1">
                            <CalendarIcon className="h-3 w-3 mr-1" />
                            Start Time
                          </div>
                          <p className="font-medium text-gray-900">
                            {formatDate(reservation.startTime)}
                          </p>
                        </div>
                      </div>
                      
                      <div className="mt-3 text-sm">
                        <p className={`${overdue ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
                          Expected Return: {calculateExpectedReturn(reservation.startTime, reservation.reservationDuration)}
                        </p>
                        {overdue && showOverdueWarning && (
                          <p className="text-red-600 font-medium mt-1 flex items-center">
                            <ExclamationTriangleIcon className="h-4 w-4 mr-1" />
                            This reservation is overdue and may incur additional charges
                          </p>
                        )}
                      </div>
                    </div>
                    
                    <div className="ml-4">
                      <Link
                        to={`/reservations`}
                        className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <EyeIcon className="h-4 w-4 mr-1" />
                        View Details
                      </Link>
                    </div>
                  </div>
                </div>
              );
            })}
            
            {reservations.length > maxItems && (
              <div className="text-center pt-4">
                <Link
                  to="/reservations?status=active"
                  className="text-blue-600 hover:text-blue-800 font-medium"
                >
                  View All Active Reservations ({reservations.length - maxItems} more) →
                </Link>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ActiveReservationsList;